import * as Y from "yjs";
import type { Point, Stroke } from "./types";

export const INK_KEY = "ink";

export function inkArray(doc: Y.Doc): Y.Array<Stroke> {
  return doc.getArray<Stroke>(INK_KEY);
}

export function readStrokes(doc: Y.Doc): Stroke[] {
  return inkArray(doc).toArray();
}

export function addStroke(doc: Y.Doc, stroke: Stroke): void {
  inkArray(doc).push([stroke]);
}

function distanceToSegment(point: Point, a: Point, b: Point): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lengthSquared = dx * dx + dy * dy;
  if (lengthSquared === 0) return Math.hypot(point.x - a.x, point.y - a.y);
  const t = Math.max(0, Math.min(1, ((point.x - a.x) * dx + (point.y - a.y) * dy) / lengthSquared));
  return Math.hypot(point.x - (a.x + t * dx), point.y - (a.y + t * dy));
}

export function strokeHitsPoint(stroke: Stroke, point: Point, tolerance: number): boolean {
  const reach = tolerance + stroke.width / 2;
  if (stroke.points.length === 1) {
    return Math.hypot(point.x - stroke.points[0].x, point.y - stroke.points[0].y) <= reach;
  }
  for (let i = 1; i < stroke.points.length; i += 1) {
    if (distanceToSegment(point, stroke.points[i - 1], stroke.points[i]) <= reach) return true;
  }
  return false;
}

export function eraseStrokesNear(doc: Y.Doc, point: Point, tolerance: number): number {
  const array = inkArray(doc);
  let removed = 0;
  doc.transact(() => {
    for (let index = array.length - 1; index >= 0; index -= 1) {
      if (strokeHitsPoint(array.get(index), point, tolerance)) {
        array.delete(index, 1);
        removed += 1;
      }
    }
  });
  return removed;
}

export function strokePath(stroke: Stroke): string {
  const [first, ...rest] = stroke.points;
  if (!first) return "";
  if (rest.length === 0) return `M ${first.x} ${first.y} L ${first.x + 0.01} ${first.y}`;
  return `M ${first.x} ${first.y} ${rest.map((point) => `L ${point.x} ${point.y}`).join(" ")}`;
}

export function strokeBounds(strokes: Stroke[]) {
  const points = strokes.flatMap((stroke) => stroke.points);
  if (points.length === 0) return null;
  const xs = points.map((point) => point.x);
  const ys = points.map((point) => point.y);
  const x = Math.min(...xs);
  const y = Math.min(...ys);
  return { x, y, w: Math.max(...xs) - x, h: Math.max(...ys) - y };
}
